import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Link,
  Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PageSeo from '../components/PageSeo';
import BrandHeroEyebrow from '../components/BrandHeroEyebrow';
import { brandHeroSection, heroContentZ } from '../theme/heroStyles';

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      image: '/assets/diesel-fuel-pump.png',
      title: 'Diesel Pump Repairs',
      description: 'Expert repair and maintenance services for diesel pumps, injectors and turbochargers.',
      benefits: ['Fuel Injection Repair', 'Injector Maintenance', 'Turbocharger Service'],
    },
    {
      image: '/assets/WhatsApp Image 2025-11-07 at 15.24.43_d0e402b4.jpg',
      title: 'Engine Overhauls',
      description: 'Complete engine overhaul services for all makes of diesel vehicles and machinery.',
      benefits: ['Engine Rebuilds', 'Cylinder Head Service', 'Performance Testing'],
    },
    {
      image: '/assets/diagnosticServices.jpg',
      title: 'Diagnostic Services',
      description: 'Advanced electronic diagnostic troubleshooting and repair services for modern diesel systems.',
      benefits: ['ECU Diagnostics', 'Fault Code Reading', 'System Programming'],
    },
    {
      image: '/assets/qualityGuarantee.jpg',
      title: 'Quality Guarantee',
      description: 'Six months on labour and new parts fitted.',
      benefits: ['Six months on labour and new parts fitted', 'Quality Workmanship', 'RMI Certified'],
    },
  ];

  const stats = [
    { number: '1969', label: 'Established' },
    { number: '50+', label: 'Years Experience' },
    { number: 'RMI', label: 'Certified' },
    { number: 'Six months', label: 'Labour & parts warranty' },
  ];

  const quickServiceLinks = [
    { label: 'Pump & injector repair', to: '/services#diesel-pump-repair' },
    { label: 'Engine overhauls', to: '/services#engine-overhaul' },
    { label: 'ECU diagnostics', to: '/services#electronic-diagnostics' },
    { label: 'Injector coding & turbos', to: '/services#injectors-turbo-chargers' },
  ];

  const heroEquipment = [
    {
      src: '/assets/diesel-fuel-pump.png',
      alt: 'High-pressure diesel fuel injection pump repaired at Diesel City',
      label: 'Fuel pumps',
      accent: 'secondary',
    },
    {
      src: '/assets/diesel-injectors-set.png',
      alt: 'Set of diesel fuel injectors serviced and calibrated',
      label: 'Injector sets',
      accent: 'primary',
    },
    {
      src: '/assets/diesel-turbocharger.png',
      alt: 'Garrett turbocharger serviced at Diesel City',
      label: 'Turbochargers',
      accent: 'secondary',
    },
  ];

  const galleryItems = [
    { src: '/assets/diesel-fuel-pump.png', alt: 'Diesel fuel injection pump on the bench at Diesel City' },
    { src: '/assets/diesel-injectors-set.png', alt: 'Set of diesel injectors ready for service or calibration' },
    { src: '/assets/diesel-turbocharger.png', alt: 'Turbocharger repair and service at Diesel City' },
    { src: '/assets/WhatsApp Image 2025-11-07 at 15.24.43_d0e402b4.jpg', alt: 'Diesel engine overhaul work at Diesel City workshop' },
    { src: '/assets/IMG-20251107-WA0023.jpg', alt: 'Diesel fuel injection pump and injector components' },
    { src: '/assets/IMG-20251107-WA0015.jpg', alt: 'Heavy diesel machinery fuel system service' },
    { src: '/assets/IMG-20251107-WA0013.jpg', alt: 'Diesel injector testing and calibration bench' },
    { src: '/assets/IMG-20251107-WA0012.jpg', alt: 'Commercial diesel vehicle in workshop for repair' },
    { src: '/assets/IMG-20251107-WA0019.jpg', alt: 'Diesel City technicians servicing injection equipment' },
  ];

  return (
    <>
      <PageSeo
        pathname="/"
        title="Diesel City | Expert Diesel Services Since 1969 | Johannesburg"
        description="RMI-certified diesel workshop in Selby, Johannesburg: pump and injector repair, engine overhauls, ECU diagnostics and turbo service. Established 1969."
        ogTitle="Diesel City | Diesel Fuel Injection Specialists"
        ogDescription="Expert diesel pump repair, engine overhauls, injector service and diagnostics for vehicles and earth-moving equipment in Gauteng."
      />

      <Box
        sx={{
          ...brandHeroSection(theme, '/assets/frontReplace.jpeg'),
          pb: { xs: 10, md: 12 },
        }}
      >
        <Container maxWidth="lg" sx={heroContentZ}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <BrandHeroEyebrow label="Expert diesel · Since 1969" />
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                component="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.5,
                  color: '#fff',
                  textShadow: '0 4px 32px rgba(0,0,0,0.35)',
                }}
              >
                Diesel City
              </Typography>
              <Typography
                variant={isMobile ? 'h5' : 'h3'}
                component="p"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: 3,
                  background: `linear-gradient(100deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 45%, ${theme.palette.secondary.dark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 2px 12px rgba(241, 196, 0, 0.35))',
                }}
              >
                Diesel fuel injection specialists
              </Typography>
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{
                  mb: 3,
                  opacity: 0.94,
                  lineHeight: 1.7,
                  maxWidth: 520,
                  fontWeight: 400,
                  fontSize: { md: '1.125rem' },
                }}
              >
                State-of-the-art equipment and workmanship for pumps, injectors, overhauls and diagnostics — Selby,
                Johannesburg, serving Gauteng.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
                <Button
                  component={RouterLink}
                  to="/services"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                >
                  Explore services
                </Button>
                <Button component={RouterLink} to="/contact" variant="outlined" size="large" color="inherit">
                  Book / enquire
                </Button>
                <Button href="tel:+27114936226" variant="outlined" size="large" color="inherit" startIcon={<PhoneIcon />}>
                  Call 011 493 6226
                </Button>
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.88, mb: 2 }}>
                Urgent mobile:{' '}
                <Link href="tel:+27812467394" color="secondary.light" fontWeight={700} underline="hover">
                  081 246 7394
                </Link>
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                {quickServiceLinks.map((item) => (
                  <Chip
                    key={item.to}
                    label={item.label}
                    component={RouterLink}
                    to={item.to}
                    clickable
                    variant="outlined"
                    sx={{
                      borderColor: alpha('#fff', 0.45),
                      color: 'common.white',
                      fontWeight: 600,
                      borderRadius: '999px',
                      '&:hover': {
                        borderColor: 'secondary.main',
                        bgcolor: alpha(theme.palette.secondary.main, 0.12),
                      },
                    }}
                  />
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: { xs: 280, md: 440 },
                  position: 'relative',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    width: { xs: '88%', md: '92%' },
                    height: { xs: '88%', md: '90%' },
                    borderRadius: 5,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, rgba(255,255,255,0.5), ${theme.palette.primary.light})`,
                    opacity: 0.65,
                    filter: 'blur(2px)',
                    transform: 'rotate(-3deg)',
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 560,
                    p: '5px',
                    borderRadius: 4,
                    background: `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.95)} 0%, rgba(255,255,255,0.55) 42%, ${alpha(theme.palette.primary.light, 0.9)} 100%)`,
                    boxShadow: '0 40px 80px rgba(0,0,0,0.45)',
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      bgcolor: alpha('#000', 0.15),
                      border: `1px solid ${alpha('#fff', 0.2)}`,
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/frontReplace.jpeg"
                      alt="Diesel City workshop building — diesel fuel injection specialists in Johannesburg"
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        objectFit: 'cover',
                        transform: 'scale(1.02)',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 4, md: 5 },
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.04)} 0%, ${theme.palette.background.default} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2.5} justifyContent="center">
            {heroEquipment.map((item) => {
              const isYellow = item.accent === 'secondary';
              const accentMain = isYellow ? theme.palette.secondary : theme.palette.primary;
              const accentLight = isYellow ? theme.palette.secondary.light : theme.palette.primary.light;
              const frameGradient = isYellow
                ? `linear-gradient(135deg, ${accentMain.main} 0%, #fff4b8 50%, ${accentLight} 100%)`
                : `linear-gradient(135deg, ${accentLight} 0%, #c8d0ff 45%, ${accentMain.main} 100%)`;

              return (
                <Grid item xs={12} sm={4} key={item.src}>
                  <Box
                    sx={{
                      height: '100%',
                      p: '5px',
                      borderRadius: 3,
                      background: frameGradient,
                      boxShadow: `0 20px 48px ${alpha(accentMain.main, 0.2)}`,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                        minHeight: { xs: 240, sm: 280, md: 320 },
                      }}
                    >
                      <Box
                        role="img"
                        aria-label={item.alt}
                        sx={{
                          flex: 1,
                          width: '100%',
                          minHeight: { xs: 200, sm: 240, md: 280 },
                          backgroundImage: `url(${item.src})`,
                          backgroundSize: 'contain',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          bgcolor: isYellow ? '#fff9e0' : '#e8ecff',
                        }}
                      />
                      <Box
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          textAlign: 'center',
                          bgcolor: accentMain.main,
                          color: accentMain.contrastText,
                          flexShrink: 0,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: 2.75,
          background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.contrastText',
          borderTop: `3px solid ${theme.palette.secondary.main}`,
          boxShadow: '0 12px 32px rgba(15, 19, 112, 0.2)',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <PhoneIcon sx={{ color: 'secondary.main' }} aria-hidden />
              <Link href="tel:+27114936226" color="inherit" fontWeight={700} underline="hover">
                011 493 6226
              </Link>
              <Typography component="span" variant="body2" sx={{ opacity: 0.75, display: { xs: 'none', md: 'inline' } }}>
                ·
              </Typography>
              <Link
                href="tel:+27812467394"
                color="inherit"
                fontWeight={700}
                underline="hover"
                sx={{ display: { xs: 'none', md: 'inline' } }}
              >
                081 246 7394
              </Link>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ maxWidth: { md: '58%' } }}>
              <AccessTimeIcon sx={{ color: 'secondary.main', flexShrink: 0, mt: 0.25 }} aria-hidden />
              <Typography variant="body2" sx={{ opacity: 0.92, lineHeight: 1.55, fontWeight: 500 }}>
                Mon–Thu 07:00–16:30 · Fri 07:00–15:30 ·{' '}
                <Link component={RouterLink} to="/contact#faqs" color="secondary.light" fontWeight={700} underline="hover">
                  FAQs & directions
                </Link>
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 6, md: 9 },
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 40%), ${theme.palette.background.default}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2.5}>
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={stat.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    textAlign: 'center',
                    borderRadius: 3,
                    height: '100%',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    borderTop: `4px solid ${index % 2 === 0 ? theme.palette.secondary.main : theme.palette.primary.main}`,
                    background:
                      index % 2 === 0
                        ? `linear-gradient(165deg, #fffef5 0%, #fff 45%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`
                        : `linear-gradient(165deg, #f5f7ff 0%, #fff 45%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
                    boxShadow: '0 16px 40px rgba(15, 19, 112, 0.08)',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 24px 48px rgba(15, 19, 112, 0.14), 0 0 0 2px ${alpha(index % 2 === 0 ? theme.palette.secondary.main : theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      mb: 1,
                      fontSize: { xs: '2rem', md: '2.75rem' },
                      background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      fontSize: { xs: '0.8rem', md: '0.95rem' },
                      lineHeight: 1.35,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, maxWidth: 720, mx: 'auto' }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 800,
                letterSpacing: '0.2em',
                display: 'block',
                mb: 1.5,
              }}
            >
              Why us
            </Typography>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 800 }}>
              Built for hard-working diesel
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, lineHeight: 1.6 }}>
              Trucks, plant and generators — warranty-backed work and RMI membership you can verify.
            </Typography>
          </Box>

          <Grid container spacing={3.5}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={feature.title}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    overflow: 'visible',
                    borderTop: `4px solid ${index % 2 === 0 ? theme.palette.secondary.main : theme.palette.primary.main}`,
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: `0 28px 56px rgba(15, 19, 112, 0.14), 0 0 0 2px ${alpha(index % 2 === 0 ? theme.palette.secondary.main : theme.palette.primary.main, 0.25)}`,
                    },
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  }}
                >
                  <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                        mb: 2.5,
                        background:
                          index % 2 === 0
                            ? `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.22)} 0%, #fff9e6 40%, #fff 100%)`
                            : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, #eef1ff 40%, #fff 100%)`,
                      }}
                    >
                      <Box
                        component="img"
                        src={feature.image}
                        alt={feature.title}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 800,
                        mb: 1.5,
                        fontSize: { xs: '1.05rem', md: '1.2rem' },
                        minHeight: { xs: '2.75rem', md: '3.25rem' },
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2.5,
                        lineHeight: 1.65,
                        minHeight: { xs: '4.25rem', md: '4.75rem' },
                      }}
                    >
                      {feature.description}
                    </Typography>
                    <Box sx={{ textAlign: 'left', mt: 'auto' }}>
                      <Stack spacing={1}>
                        {feature.benefits.map((benefit, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0, mt: 0.2 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              {benefit}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 8, md: 11 },
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.06)} 0%, ${theme.palette.background.paper} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.18em' }}>
              Workshop floor
            </Typography>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 800, mt: 1 }}>
              Our work in action
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 640, mx: 'auto', fontWeight: 400 }}>
              Real jobs from our Johannesburg bays — pumps, injectors and engines.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {galleryItems.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={item.src}>
                <Box
                  sx={{
                    width: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    boxShadow: '0 12px 32px rgba(15, 19, 112, 0.07)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px) scale(1.01)',
                      boxShadow: `0 24px 48px rgba(15, 19, 112, 0.14), 0 0 0 3px ${alpha(theme.palette.secondary.main, 0.35)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      minHeight: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.05)}, #fff)`,
                    }}
                  >
                    <Box
                      component="img"
                      src={item.src}
                      alt={item.alt}
                      loading={index > 2 ? 'lazy' : undefined}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 260,
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 9, md: 12 },
          color: 'primary.contrastText',
          background: `linear-gradient(125deg, #080a2e 0%, ${theme.palette.primary.dark} 40%, ${theme.palette.primary.main} 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'url(/assets/IMG-20250819-WA0054.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.14,
            mixBlendMode: 'overlay',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(ellipse 80% 60% at 50% 120%, ${alpha(theme.palette.secondary.main, 0.25)}, transparent 55%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{ color: 'secondary.main', fontWeight: 800, letterSpacing: '0.22em', display: 'block', mb: 2 }}
          >
            Ready when you are
          </Typography>
          <Typography variant="h2" gutterBottom sx={{ fontWeight: 800, textShadow: '0 4px 24px rgba(0,0,0,0.35)' }}>
            Problems with a diesel vehicle, generator or earth mover?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.92, fontWeight: 400, lineHeight: 1.65 }}>
            Tell us what you are fighting — rough running, smoke, loss of power or pump noise — and we will chart the
            fix.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 5, py: 1.75, fontWeight: 800, fontSize: '1.1rem' }}
            >
              Contact us today
            </Button>
            <Button href="tel:+27114936226" variant="outlined" color="inherit" size="large" startIcon={<PhoneIcon />}>
              Call the workshop
            </Button>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default HomePage;
